import { useMemo } from "react";
import { useResource, formatDate, formatDateTime, daysUntil, titleCase } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import type { Incident, Participant, ParticipantBudget, ServiceNote, Shift } from "../lib/types";

/**
 * The care-side summary for one participant: plan window, funding budgets with
 * this week's rostered commitment, recent notes and open incidents — the
 * participant ↔ roster ↔ notes ↔ incidents link.
 *
 * Notes come from the existing service_note table (/service-notes), NOT a
 * progress_note table (there isn't one). Funding renders only for supervisor
 * and above — the API enforces it via RLS; the role check here just avoids
 * firing a request that would 403. The web ServiceNote type omits created_at
 * (present at runtime), so widen locally.
 */
type Note = ServiceNote & { created_at?: string };

const local = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const weekRange = (): { from: string; to: string } => {
    // Monday of the current week, LOCAL dates — never toISOString (UTC+8 bug).
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    // The /shifts endpoint compares against timestamptz (ends_at >= from,
    // starts_at <= to), same shape Rostering.tsx uses.
    return { from: `${local(monday)}T00:00:00Z`, to: `${local(sunday)}T23:59:59Z` };
};

const planChip = (planEnd: string | null): { label: string; tone: string } | null => {
    if (!planEnd) return null;
    const days = daysUntil(planEnd);
    if (days < 0) return { label: "Plan expired", tone: "block" };
    if (days <= 60) return { label: `Expires in ${days} days`, tone: "warn" };
    return { label: `${days} days left`, tone: "pass" };
};

export const ParticipantCareSummary = ({ participant }: { participant: Participant }) => {
    const { user } = useAuth();
    const canSeeFunding =
        user?.role === "admin" || user?.role === "manager" || user?.role === "supervisor";

    const budgets = useResource<ParticipantBudget[]>(
        canSeeFunding ? `/participant-budgets?participant_id=${participant.id}` : null
    );
    const notes = useResource<Note[]>(`/service-notes?participant_id=${participant.id}`);
    const incidents = useResource<Incident[]>(`/incidents?participant_id=${participant.id}`);

    // /shifts has no participant filter — fetch the week and narrow client-side.
    const { from, to } = weekRange();
    const shifts = useResource<Shift[]>(`/shifts?from=${from}&to=${to}`);

    const weeklyHours = useMemo(
        () =>
            (shifts.data ?? [])
                .filter((s) => s.participant_id === participant.id && s.status !== "cancelled")
                .reduce((sum, s) => {
                    const ms = new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime();
                    return sum + Math.max(0, ms) / 3_600_000;
                }, 0),
        [shifts.data, participant.id]
    );

    const openIncidents = (incidents.data ?? []).filter(
        (i) => i.status === "open" || i.status === "under_review"
    );
    const chip = planChip(participant.plan_end_date);

    return (
        <div className="care-summary grid cols-2">
            <div className="card">
                <div className="card-head"><h2>NDIS plan</h2></div>
                <div className="card-body">
                    <p>
                        {formatDate(participant.plan_start_date)} – {formatDate(participant.plan_end_date)}
                        {chip && <span className={`chip ${chip.tone}`} style={{ marginLeft: 6 }}>{chip.label}</span>}
                    </p>
                    {canSeeFunding && (
                        <>
                            <h3 style={{ margin: "0.9rem 0 0.4rem" }}>Funding budgets</h3>
                            {(budgets.data ?? []).map((b) => {
                                const pct = b.allocated > 0 ? Math.min(100, (Number(b.spent) / Number(b.allocated)) * 100) : 0;
                                const tone = pct >= 95 ? "block" : pct >= 85 ? "warn" : "pass";
                                return (
                                    <div key={b.id} className="budget-row">
                                        <div className="budget-head">
                                            <span>{b.category}</span>
                                            <span>${Number(b.spent).toLocaleString()} / ${Number(b.allocated).toLocaleString()}</span>
                                        </div>
                                        <div className="budget-bar">
                                            <div className={`budget-fill ${tone}`} style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                            {!budgets.data?.length && <p className="empty">No budgets recorded.</p>}
                            <p className="sub">Rostered this week: <b>{weeklyHours.toFixed(1)} h</b></p>
                        </>
                    )}
                </div>
            </div>

            <div className="card">
                <div className="card-head"><h2>Open incidents ({openIncidents.length})</h2></div>
                <div className="card-body">
                    {openIncidents.map((i) => (
                        <p key={i.id}>
                            <span className={`chip ${i.severity === "critical" || i.severity === "high" ? "block" : "warn"}`}>
                                {titleCase(i.severity)}
                            </span>{" "}
                            {i.title} · {titleCase(i.status)}
                        </p>
                    ))}
                    {!openIncidents.length && <p className="empty">None open.</p>}

                    <h3 style={{ margin: "0.9rem 0 0.4rem" }}>Recent notes</h3>
                    {(notes.data ?? []).slice(0, 5).map((n) => (
                        <p key={n.id}>
                            <span className="sub">{formatDateTime(n.created_at ?? n.note_date)} · {titleCase(n.kind.replace(/_/g, " "))}</span>
                            <br />
                            {n.body.length > 140 ? `${n.body.slice(0, 140)}…` : n.body}
                        </p>
                    ))}
                    {!notes.data?.length && <p className="empty">No notes yet.</p>}
                </div>
            </div>
        </div>
    );
};
